import React, {useEffect} from "react";
import "./NavMenu.css";
import {useDispatch, useSelector} from "react-redux";
import {RootState} from "../../store/rootReducer";
import {useGetCatalogsQuery, useGetRecentWorkspaceRefsQuery, useSetSelectedWorkspaceMutation} from "../../store/Api";
import CreateWorkspaceDialog from "../Workspace/CreateWorkspaceDialog";
import WorkspaceSearchModal from "../Workspace/WorkspaceSearchModal";
import {
    useGetOrganizationsQuery,
    useGetOrganizationQuery,
    useGetOrganizationEntitlementsQuery
} from "../../store/endpoints/OrganizationsEndpoints"
import {
    CommandBar, CommandBarButton, ContextualMenuItemType, ICommandBarItemProps,
    IStyle,
    Layer,
    SearchBox,
    Spinner, useTheme} from '@fluentui/react';
import {useHistory} from "react-router-dom";
import UserDropdown from "./UserDropdown";
import {tracker} from "../../store/store";
import useCan, {LLM_ANALYSIS, PROCUREMENT, STOREFRONT_ADMIN} from "../../permissions/ability";
import AIPanel from "../AdaptiveIntelligence/AIPanel";
import AssistantSessionsPanel from "../AdaptiveIntelligence/AssistantSessionsPanel";
import useAssistantAvailable from "../AdaptiveIntelligence/Assistant/useAssistantAvailable";
import GettingStartedButton from "../GettingStarted/GettingStartedButton";
import useGettingStarted from "../GettingStarted/useGettingStarted";
import {updateSelectedCatalog} from "../../store/searchSlice";
import {IOrganization} from "../../models/Settings";
import TasksDropdown from "./TasksDropdown";
import useSwitchOrganization from "../../hooks/useSwitchOrganization";
import GlobalSearchTrigger from "../GlobalSearch/GlobalSearchTrigger";
import GlobalSearchPalette from "../GlobalSearch/GlobalSearchPalette";
import CreateEntityHost from "../GlobalSearch/CreateEntityHost";
import {IGlobalSearchOpenOptions, useEntityOpenRequests, useGlobalSearchHotkey, useGlobalSearchOpenRequests} from "../GlobalSearch/useGlobalSearchHotkey";
import {EntityRequest, IGlobalSearchContext} from "../GlobalSearch/sections";
import SubOrganizationSwitcher from "../SubOrganization/SubOrganizationSwitcher";

const NavMenu: React.FC = () => {
    const dispatch = useDispatch();
    const auth = useSelector((state: RootState) => state.auth);

    const getOrganizations = useGetOrganizationsQuery({username: auth.userName ?? ''}, {skip: !auth.userName || auth.userName === '' || !!auth.supportSession});
    const switchOrganization = useSwitchOrganization();
    const selectedOrganization = useSelector((state: RootState) => state.settings.selectedOrganization);
    // Set by the search page while scrolled past its inline search box; the page portals its
    // ProductSearchBox + pager into the dock divs below. Cleared on the page's unmount.
    const searchBarDocked = useSelector((state: RootState) => state.search.searchBarDocked === true);
    const history = useHistory();
    const canAccessProcurement = useCan(PROCUREMENT)
    const canStorefrontAdmin = useCan(STOREFRONT_ADMIN)
    const canAI = useCan(LLM_ANALYSIS)
    // The Adaptive Intelligence sessions panel and the older AI chat panel are separate surfaces and
    // have their own buttons.
    const assistantAvailable = useAssistantAvailable();
    const [aiPanel, setAIPanel] = React.useState<boolean>(false);
    const [assistantSessions, setAssistantSessions] = React.useState<boolean>(false);
    // Onboarding checklist. The hook shares its RTK Query subscription with the button, and reports
    // isVisible=false when the endpoint is missing or errors, so the far command is simply absent.
    const gettingStarted = useGettingStarted();
    const [orgSearchText, setOrgSearchText] = React.useState('');
    const [selectedOrg , setSelectedOrg] = React.useState<IOrganization>();
    // Three layouts: full (>1100), condensed single row (701-1100: wand icon only, icon-only nav
    // buttons so the CommandBar never folds anything into its "..." overflow), and two rows
    // (<=700: logos + wand + tasks/user on top, nav buttons with labels below).
    const [isCondensed, setIsCondensed] = React.useState(window.innerWidth <= 1100);
    const [isTwoRow, setIsTwoRow] = React.useState(window.innerWidth <= 700);
    const [paletteOpen, setPaletteOpen] = React.useState(false);
    // The Workspace nav button is a split button: click goes to your last workspace, the chevron
    // opens recent workspaces, More..., and Create New (moved here from the workspace page's toolbar).
    const [createWorkspaceOpen, setCreateWorkspaceOpen] = React.useState(false);
    const [workspaceSearchOpen, setWorkspaceSearchOpen] = React.useState(false);
    const recentWorkspaces = useGetRecentWorkspaceRefsQuery();
    const lastWorkspaceName = recentWorkspaces.data?.[0]?.name;
    const [setSelectedWorkspace] = useSetSelectedWorkspaceMutation();
    const goToWorkspace = (id: string) => {
        void setSelectedWorkspace(id);
        history.push(`/workspace/${id}`);
    };
    const [paletteContext, setPaletteContext] = React.useState<IGlobalSearchContext | undefined>();
    const [paletteMode, setPaletteMode] = React.useState<'products' | undefined>();
    const [entityRequest, setEntityRequest] = React.useState<EntityRequest | undefined>();
    // The palette takes its context from wherever it was opened: on /Workspace/{id} product rows
    // gain an in-place "Add to this workspace" and the empty query shows suggestions for it.
    const lastWorkspace = recentWorkspaces.data?.[0];
    const openPalette = React.useCallback((options: IGlobalSearchOpenOptions = {}) => {
        const match = /\/workspace\/([^/?#]+)/i.exec(window.location.pathname);
        let context = options.context;
        if (!context && match) {
            const id = decodeURIComponent(match[1]);
            // /workspace/last is the page's alias for the most recent workspace, not an id: resolve it the
            // same way the page does (or carry no context until the recent list has loaded).
            if (id.toLowerCase() === 'last') {
                if (lastWorkspace?.id) context = { kind: 'workspace', workspaceId: lastWorkspace.id, name: lastWorkspace.name };
            } else {
                context = { kind: 'workspace', workspaceId: id };
            }
        }
        setPaletteContext(context);
        setPaletteMode(options.mode);
        setPaletteOpen(true);
    }, [lastWorkspace?.id, lastWorkspace?.name]);

    useEffect(() => {
        const handler = () => {
            setIsCondensed(window.innerWidth <= 1100);
            setIsTwoRow(window.innerWidth <= 700);
        };
        window.addEventListener('resize', handler);
        return () => window.removeEventListener('resize', handler);
    }, []);

    useGlobalSearchHotkey(() => openPalette());
    useGlobalSearchOpenRequests(openPalette);
    // Entity requests raised outside the palette (e.g. a grid thumbnail opening a product's images).
    useEntityOpenRequests(setEntityRequest);
    const getCatalogs = useGetCatalogsQuery();
    const whiteLabel = window.config.overrideLogo;
    const title = window.config.title ?? "";

    const getOrganization = useGetOrganizationQuery(selectedOrganization ?? '', {skip: !selectedOrganization || selectedOrganization === ''});
    const getOrgEntitlements = useGetOrganizationEntitlementsQuery(selectedOrganization ?? '', {skip: !selectedOrganization || selectedOrganization === ''});

    const handleResetCache = (org: string) => {
        switchOrganization(org);
    };

    useEffect(() => {
        if(getOrganization.data){
            setSelectedOrg(getOrganization.data);
        }
    }, [getOrganization.data]);


    const navMenuStyle: IStyle = {
        padding: 0,
        height: '48px',
        borderBottom: '1px solid #ccc',
        boxShadow: '0 1px 2px 0 rgba(34,36,38,.15)',
    }

    const navMenuTopRowStyle: IStyle = {
        padding: 0,
        height: '48px',
        borderBottom: '1px solid #eee',
    }

    const buttonStyle: IStyle = {
    }

    const imageStyle: React.CSSProperties = {
        padding: '3px',
        height: '35px'
    }

    const openAIPanel = () => {
        setAIPanel(true);
    }
    const closeAIPanel = () => {
        setAIPanel(false);
    }

    let orgDisplay: ICommandBarItemProps = {
        key: 'organizations',
        text: 'Loading',
        iconOnly: true,
        ariaLabel: 'Organization switcher',
        onRenderIcon: () => <Spinner/>
    }

    useEffect(() => {
        if(getCatalogs.data && selectedOrg) {
            const currentCatalog = getCatalogs?.data.find(a => a.name == selectedOrg.defaultCatalog)
            dispatch(updateSelectedCatalog(currentCatalog));
        }
    }, [selectedOrg, getCatalogs.data])

    if (auth.supportSession) {
        // A support session is bound to one organization and has no list to load (the lookup is skipped above),
        // so show that organization and offer nothing to switch to.
        orgDisplay = {
            key: 'organizations',
            text: auth.supportSession.organizationName ?? auth.supportSession.organization,
            iconOnly: true,
            ariaLabel: 'Organization',
            onRenderIcon: () => selectedOrg?.logo ? <img
                alt={`${selectedOrg.name} logo`}
                src={selectedOrg.logo}
                style={imageStyle}
            /> : null,
        }
    }
    else if (!getOrganizations.isLoading && getOrganizations.data) {
        tracker.setMetadata('organization', selectedOrg?.id ?? 'No Organization');

        const isSearchable = getOrganizations.data.length > 5;
        const sortedOrgs = getOrganizations.data
            .slice()
            .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
        const filteredOrgs = isSearchable && orgSearchText
            ? sortedOrgs.filter(org => {
                const lower = orgSearchText.toLowerCase();
                return org.name.toLowerCase().includes(lower) || org.id.toLowerCase().includes(lower);
            })
            : sortedOrgs;

        orgDisplay = {
            key: 'organizations',
            text: selectedOrg?.name ?? 'Organization',
            iconOnly: true,
            ariaLabel: 'Organization switcher',
            onRenderIcon: () => selectedOrg?.logo ? <img
                alt={`${selectedOrg.name} logo`}
                src={selectedOrg.logo}
                style={imageStyle}
            /> : null,
            subMenuProps: getOrganizations.data.length > 1 ? {
                items: filteredOrgs.length > 0
                    ? filteredOrgs.map(org => ({
                        key: org.id,
                        text: `${org.name} - ${org.id}`,
                        onClick: () => {
                            handleResetCache(org.id);
                            setOrgSearchText('');
                        }
                    }))
                    : [{ key: 'no-results', text: 'No results found', disabled: true }],
                ...(isSearchable ? {
                    onRenderMenuList: (props: any, defaultRender: any) => (
                        <>
                            <SearchBox
                                placeholder="Search organizations..."
                                value={orgSearchText}
                                onChange={(_, v) => setOrgSearchText(v ?? '')}
                                styles={{ root: { margin: '4px 8px 8px 8px' } }}
                            />
                            {defaultRender?.(props)}
                        </>
                    )
                } : {})
            } : undefined,
        }

    }

    // The dock target div is ALWAYS mounted (hidden via CSS while empty) and the docked
    // cacheKey never changes: a cacheKey flip makes CommandBar's ResizeGroup re-measure and
    // remount the item, which vanishes the portal target for a visible beat mid-scroll. With
    // a stable item, docking is just the trigger hiding and the portal filling the div —
    // same-frame, no gap. Sizing/visibility rules live in NavMenu.css.
    const globalSearchCommand: ICommandBarItemProps = {
        key: 'globalSearch',
        // The trigger's rendered width flips between the full pill and an icon at the responsive
        // breakpoints. CommandBar's ResizeGroup caches measurements by cacheKey, and a stale
        // full-pill width strands the item in the "..." overflow after crossing a breakpoint —
        // keying the cache on the breakpoint state forces a fresh measurement.
        cacheKey: `globalSearch-${isCondensed}-${isTwoRow}`,
        ariaLabel: 'Search',
        onRender: () => (
            <div className='header-dock-host'>
                {!searchBarDocked && <GlobalSearchTrigger compact={isCondensed} onOpen={() => openPalette()} />}
                <div id='header-search-dock' />
            </div>
        ),
    };

    // Renders nothing unless the tenant is entitled to sub-organizations and has at least one, so the
    // item is safe to keep in both the white-label and standard command sets.
    const subOrganizationCommand: ICommandBarItemProps = {
        key: 'subOrganization',
        cacheKey: 'subOrganization',
        ariaLabel: 'Customer scope',
        onRender: () => <SubOrganizationSwitcher />,
    };

    const getCommands = () => {
        let commands: ICommandBarItemProps[] = []
        if(whiteLabel){
            commands = [
                {
                    key: 'Home',
                    text: 'Home',
                    iconOnly: true,
                    iconProps: {iconName: 'Home'},
                    onClick: () => {
                        history.push('/');
                    }
                },
                {
                    key: 'Logo',
                    iconOnly: true,
                    onRenderIcon: () => <img
                        src={whiteLabel}
                        alt={title}
                        style={imageStyle}
                    />,
                    disabled: true
                },
                subOrganizationCommand,
                globalSearchCommand
            ]
        }
        else{
            commands = [
                {
                    key: 'Logo',
                    text: 'Home',
                    iconOnly: true,
                    onRenderIcon: () => <img
                        src="mainLogo.png"
                        alt='Adaptive Catalog logo'
                        style={imageStyle}
                    />,
                    onClick: () => {
                        history.push('/');
                    }
                },
                orgDisplay,
                subOrganizationCommand,
                globalSearchCommand
            ]
        }
        return commands;
    }

    const isSettingsPage = window.location.pathname.toLowerCase().indexOf('/settings') !== -1;
    const isSearchPage = !isSettingsPage && window.location.pathname.toLowerCase().indexOf('/search') !== -1;
    const isWorkspacePage = !isSettingsPage && window.location.pathname.toLowerCase().indexOf('/workspace') !== -1;
    const isProcurementPage = !isSettingsPage && window.location.pathname.toLowerCase().indexOf('/procurement') !== -1;
    const isStorefrontPage = !isSettingsPage && window.location.pathname.toLowerCase().indexOf('/storefront') !== -1;

    const getRightCommands = () => {
        const commandBuilder: ICommandBarItemProps[] = [
            // Same always-mounted dock treatment as globalSearch: while the search page is
            // scrolled, its pager portals into the div and the (redundant, we're already
            // there) Products button hides — no cacheKey change, no re-measure beat.
            {
                key: 'Search',
                cacheKey: 'Search-' + String(isCondensed && !isTwoRow),
                onRender: () => (
                    <div className='header-dock-host'>
                        {!searchBarDocked && (
                            <CommandBarButton
                                text={isCondensed && !isTwoRow ? undefined : 'Products'}
                                iconProps={{ iconName: 'Product' }}
                                title='Products'
                                styles={{
                                    root: {
                                        ...buttonStyle,
                                        height: '100%',
                                        backgroundColor: isSearchPage ? '#ddd' : 'white'
                                    }
                                }}
                                onClick={() => {
                                    history.push('/search');
                                }}
                            />
                        )}
                        <div id='header-pager-dock' />
                    </div>
                ),
            },
            {
                key: 'Workspace',
                cacheKey: 'Workspace-' + String(isCondensed && !isTwoRow) + '-' + (recentWorkspaces.data?.map(w => `${w.id}:${w.name ?? ''}`).join(',') ?? ''),
                text: 'Workspace',
                // A plain click lands on /workspace/last (the most recent workspace); the tooltip says which.
                title: lastWorkspaceName ? `Open ${lastWorkspaceName}` : 'Open your most recent workspace',
                iconProps: { iconName: 'WorkItem' },
                iconOnly: isCondensed && !isTwoRow,
                split: true,
                splitButtonAriaLabel: 'Workspace options',
                buttonStyles: {
                    root: {
                        ...buttonStyle,
                        backgroundColor: isWorkspacePage ? '#ddd' : 'white'
                    },
                    splitButtonMenuButton: {
                        ...buttonStyle,
                        backgroundColor: isWorkspacePage ? '#ddd' : 'white'
                    }
                },
                onClick: () => {
                    history.push('/workspace/last');
                },
                subMenuProps: {
                    items: [
                        { key: 'recentHeader', text: 'Recent Workspaces', itemType: ContextualMenuItemType.Header },
                        ...(recentWorkspaces.data ?? []).map((workspace) => ({
                            key: workspace.id ?? '',
                            text: `${workspace.name ?? 'Workspace'}${workspace.active ? '' : ' (Inactive)'}`,
                            iconProps: { iconName: 'WorkItem' },
                            onClick: () => { if (workspace.id) goToWorkspace(workspace.id); },
                        })),
                        { key: 'divider1', itemType: ContextualMenuItemType.Divider },
                        { key: 'moreWorkspaces', text: 'More...', iconProps: { iconName: 'Search' }, onClick: () => setWorkspaceSearchOpen(true) },
                        { key: 'divider2', itemType: ContextualMenuItemType.Divider },
                        { key: 'newWorkspace', text: 'Create New', iconProps: { iconName: 'Add' }, onClick: () => setCreateWorkspaceOpen(true) },
                    ],
                },
            },
        ]
        if(getOrgEntitlements.data?.procurementEnabled === true && canAccessProcurement){
            commandBuilder.push(                {
                key: 'Procurement',
                cacheKey: 'Procurement-' + String(isCondensed && !isTwoRow),
                text: 'Procurement',
                iconProps: { iconName: 'DocumentSet' },
                iconOnly: isCondensed && !isTwoRow,
                subMenuProps: {
                    items: [
                        {
                            key: 'procurementHome',
                            text: 'Procurement',
                            itemType: ContextualMenuItemType.Normal,
                            onClick: () => history.push('/procurement'),
                        },
                        {
                            key: "orders",
                            text: "Orders",
                            itemType: ContextualMenuItemType.Normal,
                            onClick: () => {
                                history.push('/procurement/orders')
                            }
                        },
                        {
                            key: "demands",
                            text: "Demand Items",
                            itemType: ContextualMenuItemType.Normal,
                            onClick: () => {
                                history.push('/procurement/demands/items')
                            }
                        }
                    ]
                },
                buttonStyles: {
                    root: {
                        ...buttonStyle,
                        backgroundColor: isProcurementPage ? '#ddd' : 'white'
                    }
                },
                onClick: () => {
                    history.push('/procurement');
                },
            })
        }
        if (canStorefrontAdmin) {
            commandBuilder.push({
                key: 'Storefront',
                cacheKey: 'Storefront-' + String(isCondensed && !isTwoRow),
                text: 'Storefront',
                iconProps: { iconName: 'Shop' },
                iconOnly: isCondensed && !isTwoRow,
                subMenuProps: {
                    items: [
                        {
                            key: 'storefrontDashboard',
                            text: 'Dashboard',
                            itemType: ContextualMenuItemType.Normal,
                            onClick: () => history.push('/storefront'),
                        },
                        {
                            key: 'storefrontOrders',
                            text: 'Orders',
                            itemType: ContextualMenuItemType.Normal,
                            onClick: () => history.push('/storefront/orders'),
                        },
                        {
                            key: 'storefrontCarts',
                            text: 'Carts',
                            itemType: ContextualMenuItemType.Normal,
                            onClick: () => history.push('/storefront/carts'),
                        },
                        {
                            key: 'storefrontSettingsSection',
                            itemType: ContextualMenuItemType.Section,
                            sectionProps: {
                                topDivider: true,
                                title: 'Settings',
                                items: [
                                    {
                                        key: 'storefrontOrderingSettings',
                                        text: 'Ordering & Carts',
                                        onClick: () => history.push('/settings/storefront/ordering'),
                                    },
                                    {
                                        key: 'storefrontProductsSettings',
                                        text: 'Products',
                                        onClick: () => history.push('/settings/storefront/products'),
                                    },
                                    {
                                        key: 'storefrontIntegrationsSettings',
                                        text: 'Integrations',
                                        onClick: () => history.push('/settings/storefront/integrations'),
                                    },
                                    {
                                        key: 'storefrontAppearanceSettings',
                                        text: 'Appearance',
                                        onClick: () => history.push('/settings/storefront/appearance'),
                                    },
                                    {
                                        key: 'storefrontAssetsSettings',
                                        text: 'Assets',
                                        onClick: () => history.push('/settings/storefront/assets'),
                                    },
                                    {
                                        key: 'storefrontsSettings',
                                        text: 'Storefronts',
                                        onClick: () => history.push('/settings/storefront/storefronts'),
                                    },
                                ],
                            },
                        },
                    ]
                },
                buttonStyles: {
                    root: {
                        ...buttonStyle,
                        backgroundColor: isStorefrontPage ? '#ddd' : 'white'
                    }
                },
                onClick: () => {
                    history.push('/storefront');
                },
            })
        }
        if (canAI) {
            commandBuilder.push(
                {
                    key: 'AI',
                    iconOnly: true,
                    iconProps: {iconName: 'ChatBot'},
                    onClick: openAIPanel
                }
            )
        }
        if (assistantAvailable) {
            commandBuilder.push({
                key: 'AdaptiveIntelligence',
                text: 'Adaptive Intelligence',
                ariaLabel: 'Adaptive Intelligence',
                iconOnly: true,
                iconProps: {iconName: 'Robot'},
                buttonStyles: {
                    root: {
                        ...buttonStyle,
                        backgroundColor: assistantSessions ? '#ddd' : 'white'
                    }
                },
                onClick: () => { setAssistantSessions(true); },
            })
        }

        return commandBuilder;
    };

    // Tasks + user avatar: pinned to the top-right in every layout, including the two-row one.
    const getUtilityCommands = (): ICommandBarItemProps[] => [
        // Onboarding checklist ring, ahead of tasks so it reads as a call to action rather than chrome.
        ...(gettingStarted.isVisible ? [{
            key: 'gettingStarted',
            iconOnly: true,
            ariaLabel: 'Getting Started checklist',
            onRenderIcon: () => <GettingStartedButton />,
        } as ICommandBarItemProps] : []),
        {
            key: 'tasks',
            iconOnly: true,
            onRenderIcon: () => <TasksDropdown />,
        },
        {
            key: 'Settings',
            iconOnly: true,
            onRenderIcon: () => <UserDropdown />,
            buttonStyles: {
                root: {
                    ...buttonStyle,
                    backgroundColor: isSettingsPage ? '#ddd' : 'white'
                }
            },
        },
    ];
    return (
        <header>
            <Layer>
                {/* Starts below the support-session band when there is one (see SupportSessionBanner). */}
                <div className="nav-menu" style={{marginTop: 'var(--support-band-height, 0px)'}}>
                    {isTwoRow ? (
                        <>
                            <CommandBar
                                styles={{ root: navMenuTopRowStyle }}
                                items={getCommands()}
                                farItems={getUtilityCommands()}
                            />
                            <CommandBar
                                styles={{ root: navMenuStyle }}
                                items={getRightCommands()}
                            />
                        </>
                    ) : (
                        <CommandBar
                            styles={{ root: navMenuStyle }}
                            items={getCommands()}
                            farItems={[...getRightCommands(), ...getUtilityCommands()]}
                        />
                    )}
                </div>
            </Layer>
            <AIPanel isOpen={aiPanel} onClose={closeAIPanel}/>
            <AssistantSessionsPanel isOpen={assistantSessions} onDismiss={() => setAssistantSessions(false)}/>
            <GlobalSearchPalette
                isOpen={paletteOpen}
                onDismiss={() => setPaletteOpen(false)}
                openEntity={setEntityRequest}
                context={paletteContext}
                initialMode={paletteMode}
            />
            <CreateEntityHost request={entityRequest} onDismiss={() => setEntityRequest(undefined)} />
            <CreateWorkspaceDialog
                isOpen={createWorkspaceOpen}
                onDismiss={() => setCreateWorkspaceOpen(false)}
                onCreated={(id) => { setCreateWorkspaceOpen(false); goToWorkspace(id); }}
            />
            <WorkspaceSearchModal displayModal={workspaceSearchOpen} closer={() => setWorkspaceSearchOpen(false)} />
        </header>
    );
}

export default NavMenu;